import {
    Avatar,
    Box,
    Divider,
    Grid,
    makeStyles,
    useMediaQuery,
    useTheme,
} from "@material-ui/core";
import { Role, User, UserDefault, useUsers } from "api";
import { Form, Formik } from "formik";
import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { RootState } from "redux/model";
import {
    BoxCard,
    LoggedUserContext,
    SubmitButton,
    Subtitle,
} from "ui/componentes";
import {
    yupMaxLengthField,
    yupRequiredField,
} from "ui/componentes/Form/functions/validationMessages";
import { PageLayout } from "ui/componentes/PageLayout/PageLayout";
import AddressProfileForm from "ui/views/Profile/AddressProfileForm";
import { DriverProfileForm } from "ui/views/Profile/DriverProfileForm";
import { useSaveUser } from "ui/views/Profile/hooks";
import { PasswordResetForm } from "ui/views/Profile/PasswordResetForm";
import ProfileMenu, { ProfileMenuType } from "ui/views/Profile/ProfileMenu";
import { RolesProfileForm } from "ui/views/Profile/RolesProfileForm";
import { UserProfileForm } from "ui/views/Profile/UserProfileForm";
import * as Yup from "yup";

const useStyles = makeStyles((theme) => ({
    avatar: {
        backgroundColor: theme.palette.primary.main,
        width: 90,
        height: 90,
    },
}));

const ProfileValidation = Yup.object().shape({
    firstName: Yup.string().required(yupRequiredField),
    lastName: Yup.string().required(yupRequiredField),
    driverInfo: Yup.object().shape({
        biography: Yup.string().max(300, yupMaxLengthField),
        iracingId: Yup.number().required(yupRequiredField),
    }),
});

const Profile = () => {
    const classes = useStyles();
    const { id } = useParams<{ id }>();
    const { auth } = useSelector((state: RootState) => state.firebase);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const loggedUser = useContext(LoggedUserContext);

    const saveUser = useSaveUser({
        updatePrivateInfo: true,
        updateRoles: loggedUser.roles.includes(Role.ADMIN),
    });
    const [selectedProfileMenu, setSelectedProfileMenu] = useState(
        ProfileMenuType.PROFILE
    );
    const user = useUsers<User>({
        id: id || auth.uid,
        withPrivateInfo: true,
        withRoles: true,
    });
    let userForm = { ...UserDefault, ...user };
    const disabledFields = id && id !== auth.uid && !loggedUser.isAdmin;

    return (
        <Formik
            enableReinitialize={true}
            initialValues={userForm}
            onSubmit={saveUser}
            validationSchema={disabledFields ? null : ProfileValidation}
        >
            {({ errors }) => (
                <Form>
                    <PageLayout
                        title=""
                        showInBoxCard={false}
                        boxCardProps={{ padding: isXs ? 0 : 2 }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3} lg={2}>
                                <ProfileMenu
                                    selectedMenu={selectedProfileMenu}
                                    onMenuChange={setSelectedProfileMenu}
                                />
                                <SubmitButton
                                    style={{ marginTop: 5 }}
                                    disabled={
                                        !!errors &&
                                        Object.keys(errors).length > 0
                                    }
                                >
                                    Atualizar perfil
                                </SubmitButton>
                            </Grid>
                            <Grid item xs={12} md={9} lg={10}>
                                <BoxCard>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                justifyContent="space-around"
                                                height={150}
                                            >
                                                <Avatar
                                                    className={classes.avatar}
                                                >
                                                    {userForm.initials}
                                                </Avatar>
                                                <Subtitle
                                                    text={`${userForm.displayName}`}
                                                />
                                                <Subtitle
                                                    text={
                                                        userForm.privateInfo
                                                            .email
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            hidden={
                                                selectedProfileMenu !==
                                                ProfileMenuType.PROFILE
                                            }
                                        >
                                            <UserProfileForm
                                                disabled={disabledFields}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            hidden={
                                                selectedProfileMenu !==
                                                ProfileMenuType.PROFILE
                                            }
                                        >
                                            <Divider />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            hidden={
                                                selectedProfileMenu !==
                                                ProfileMenuType.PROFILE
                                            }
                                        >
                                            <AddressProfileForm
                                                disabled={disabledFields}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            hidden={
                                                selectedProfileMenu !==
                                                ProfileMenuType.DRIVER_INFORMATION
                                            }
                                        >
                                            <DriverProfileForm
                                                disabled={disabledFields}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            hidden={
                                                selectedProfileMenu !==
                                                ProfileMenuType.ROLES
                                            }
                                        >
                                            <RolesProfileForm />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            hidden={
                                                selectedProfileMenu !==
                                                ProfileMenuType.PASSWORD_RESET
                                            }
                                        >
                                            <PasswordResetForm />
                                        </Grid>
                                    </Grid>
                                </BoxCard>
                            </Grid>
                        </Grid>
                    </PageLayout>
                </Form>
            )}
        </Formik>
    );
};

export { Profile };
